import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, Loader2, XCircle, Home } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaymentSuccess() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      // Verify via session ID (programmatic checkout)
      verifyPayment(sessionId);
    } else {
      // Payment link flow - mark user as paid directly
      markAsPaid();
    }
  }, [searchParams]);

  const verifyPayment = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/subscription/verify-payment?session_id=${sessionId}`);
      if (response.data.success) {
        setVerified(true);
        toast.success('Payment successful! Welcome to QA Builder Pro!');
      } else {
        setError(response.data.message || 'Payment verification failed');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to verify payment');
    } finally {
      setVerifying(false);
    }
  };

  const markAsPaid = async () => {
    try {
      // For payment link flow, mark user as paid
      const response = await axios.post(`${API}/subscription/confirm-payment-link`);
      if (response.data.success) {
        setVerified(true);
        toast.success('Payment successful! Welcome to QA Builder Pro!');
      } else {
        setError(response.data.message || 'Payment confirmation failed');
      }
    } catch (error) {
      // If user is already paid, consider it a success
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Already')) {
        setVerified(true);
        toast.success('You already have lifetime access!');
      } else {
        setError(error.response?.data?.detail || 'Failed to confirm payment');
      }
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          {verifying ? (
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
          ) : verified ? (
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          ) : (
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          )}
          <CardTitle className="font-heading text-2xl">
            {verifying 
              ? 'Verifying Payment...' 
              : verified 
                ? 'Payment Successful!' 
                : 'Payment Issue'
            }
          </CardTitle>
          <CardDescription>
            {verifying 
              ? 'Please wait while we confirm your payment' 
              : verified 
                ? 'Thank you for your purchase. You now have lifetime access!' 
                : error
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!verifying && (
            <Button 
              onClick={() => navigate('/dashboard')} 
              className="w-full"
              data-testid="go-dashboard-btn"
            >
              <Home className="mr-2 h-4 w-4" />
              {verified ? 'Go to Dashboard' : 'Back to App'}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
            <XCircle className="h-8 w-8 text-amber-600" />
          </div>
          <CardTitle className="font-heading text-2xl">Payment Cancelled</CardTitle>
          <CardDescription>
            No worries! You can upgrade anytime to get lifetime access.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button 
            onClick={() => navigate('/dashboard')} 
            className="w-full"
          >
            <Home className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
